import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPenToSquare, 
  faTrash,
  faSave,
  faTimes,
  faFolder
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import './Categories.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories({ paginate: false });
      const categoriesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
    }
  };

  const handleNewCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: ''
    });
    setShowEditor(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || ''
    });
    setShowEditor(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? Posts with this category will have their category unset.')) {
      try {
        await apiService.deleteCategory(categoryId);
        setCategories(categories.filter(c => c.id !== categoryId));
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(error.response?.data?.message || 'Failed to delete category');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (editingCategory) {
        const response = await apiService.updateCategory(editingCategory.id, formData);
        setCategories(categories.map(c => 
          c.id === editingCategory.id 
            ? response.data.category 
            : c
        ));
      } else {
        const response = await apiService.createCategory(formData);
        setCategories([response.data.category, ...categories]);
      }
      
      setShowEditor(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from name
    if (name === 'name' && !editingCategory) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  if (showEditor) {
    return (
      <div className="categories-editor">
        <div className="categories-editor-header">
          <h2>
            <FontAwesomeIcon icon={faFolder} style={{ marginRight: '12px', color: '#ec4899' }} />
            {editingCategory ? 'Edit Category' : 'Create New Category'}
          </h2>
          <button 
            type="button" 
            onClick={() => setShowEditor(false)}
            className="categories-btn categories-btn-secondary"
          >
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit} className="categories-form">
          <div className="categories-form-row">
            <div className="categories-form-group">
              <label htmlFor="name">Category Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter category name"
                required
              />
            </div>

            <div className="categories-form-group">
              <label htmlFor="slug">Slug *</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="category-url-slug"
                required
              />
            </div>
          </div>

          <div className="categories-form-group categories-form-group-full">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of the category"
              rows="4"
            />
          </div>

          {error && (
            <div className="categories-error">{error}</div>
          )}

          <div className="categories-form-actions">
            <button 
              type="submit" 
              className="categories-btn categories-btn-primary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
              {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
            </button>
            <button 
              type="button" 
              onClick={() => setShowEditor(false)}
              className="categories-btn categories-btn-secondary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="categories-header">
        <div>
          <h2>Categories Management</h2>
          <p>Organize your blog posts with categories</p>
        </div>
        <button onClick={handleNewCategory} className="categories-btn categories-btn-primary">
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
          New Category
        </button>
      </div>

      <div className="categories-grid">
        {categories.length === 0 ? (
          <div className="categories-empty">
            <FontAwesomeIcon icon={faFolder} style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }} />
            <p>No categories yet. Create your first category!</p>
          </div>
        ) : (
          categories.map(category => (
            <div key={category.id} className="category-card">
              <div className="category-card-header">
                <div className="category-icon">
                  <FontAwesomeIcon icon={faFolder} />
                </div>
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <span className="category-slug">{category.slug}</span>
                </div>
              </div>
              
              {category.description && (
                <p className="category-description">{category.description}</p>
              )}
              
              <div className="category-footer">
                <div className="category-stats">
                  <span className="category-count">
                    {category.posts_count || 0} {category.posts_count === 1 ? 'post' : 'posts'}
                  </span>
                </div>
                <div className="category-actions">
                  <button
                    onClick={() => handleEdit(category)}
                    className="category-action-btn category-action-edit"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="category-action-btn category-action-delete"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Categories;
